import { useState } from 'react'
import { Modal } from './Modal.tsx'
import { Combobox, type Option } from './Combobox.tsx'
import { Button, Field, Input, Select } from './ui.tsx'
import { IconPlus, IconTrash } from './icons.tsx'
import { TIMES_PER_DAY } from '@pharmacia/core'
import {
  blankItem, coursePerDay, courseTotal, daysBetween, eachDate, fmtQty,
  MAX_COURSE_DAYS, dailyTotal, type Course, type Item,
} from '../lib/courses.ts'

/** Add or change one course: a date range and the medicines taken across it. */
export function CourseEditor({ course, onSave, onClose }: {
  course: Course
  onSave: (c: Course) => void
  onClose: () => void
}) {
  const [draft, setDraft] = useState<Course>(course)
  const [error, setError] = useState<string | null>(null)

  const set = (p: Partial<Course>) => setDraft(d => ({ ...d, ...p }))
  const setItem = (key: string, p: Partial<Item>) =>
    setDraft(d => ({ ...d, items: d.items.map(i => (i.key === key ? { ...i, ...p } : i)) }))

  const span = daysBetween(draft.from, draft.to)
  const named = draft.items.filter(i => i.product)

  const save = () => {
    if (span < 1) return setError('The end date is before the start date')
    if (span > MAX_COURSE_DAYS) return setError(`That is ${span} days. A course covers at most ${MAX_COURSE_DAYS}.`)
    if (named.length === 0) return setError('Add at least one medicine')
    if (named.some(i => !(Number(i.quantity) > 0))) return setError('Every medicine needs a dose above zero')
    const ids = named.map(i => i.product!.id)
    if (new Set(ids).size !== ids.length) return setError('The same medicine is listed twice in this course')
    onSave({ ...draft, items: named })
  }

  return (
    <Modal
      title={span === 1 ? 'Course — a single day' : 'Course'}
      onClose={onClose}
      footer={
        <>
          <Button variant="primary" onClick={save}>Save course</Button>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
        </>
      }
    >
      <div className="course-grid">
        <Field label="From" htmlFor="ce-from">
          <Input id="ce-from" type="date" value={draft.from}
            onChange={e => set({ from: e.target.value })} />
        </Field>
        <Field label="To" htmlFor="ce-to" hint="Same date for a single day">
          <Input id="ce-to" type="date" value={draft.to} min={draft.from}
            onChange={e => set({ to: e.target.value })} />
        </Field>
      </div>

      <div className="form-section" style={{ marginTop: 'var(--s-4)' }}>
        <h3>Medicines in this course</h3>
        {draft.items.map((item, i) => (
          <div className="day-row" key={item.key}>
            <Combobox endpoint="/api/lookup/products" value={item.product}
              placeholder="Type a product name"
              onChange={o => setItem(item.key, { product: o })} />
            <Input value={item.quantity} inputMode="decimal"
              aria-label={`Dose each time, medicine ${i + 1}`}
              onChange={e => setItem(item.key, { quantity: e.target.value })} />
            <Select value={item.timesPerDay} aria-label={`How often, medicine ${i + 1}`}
              onChange={e => setItem(item.key, { timesPerDay: Number(e.target.value) })}>
              {TIMES_PER_DAY.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </Select>
            <button type="button" className="icon-btn danger" title="Remove medicine"
              aria-label={`Remove medicine ${i + 1}`} disabled={draft.items.length === 1}
              onClick={() => set({ items: draft.items.filter(x => x.key !== item.key) })}>
              <IconTrash size={15} />
            </button>
            <span className="day-total">
              {Number(item.quantity) > 0
                ? `= ${fmtQty(dailyTotal(item))} a day · ${fmtQty(dailyTotal(item) * Math.max(span, 0))} over the course`
                : ''}
            </span>
          </div>
        ))}
        <Button type="button" size="sm"
          onClick={() => set({ items: [...draft.items, blankItem()] })}>
          <IconPlus size={14} /> Add another medicine
        </Button>
      </div>

      <p className="course-summary">
        {span > 0 && named.length > 0 ? (
          <>
            <b>{span}</b> day{span === 1 ? '' : 's'} · <b>{fmtQty(coursePerDay(draft))}</b> a day ·
            {' '}<b>{fmtQty(courseTotal(draft))}</b> dispensed in total
          </>
        ) : 'Set the dates and at least one medicine'}
      </p>
      {error ? <p className="course-error">{error}</p> : null}
    </Modal>
  )
}

/** Read-only: what this course means, day by day. */
export function CourseDetail({ course, index, onClose }: {
  course: Course
  index: number
  onClose: () => void
}) {
  const dates = eachDate(course)
  const named = course.items.filter(i => i.product)
  const fmtDate = (iso: string) => new Intl.DateTimeFormat('en-IN',
    { day: '2-digit', month: 'short' }).format(new Date(iso + 'T00:00:00'))

  return (
    <Modal
      title={`Course ${index + 1} — ${fmtDate(course.from)} to ${fmtDate(course.to)}`}
      onClose={onClose}
      footer={<Button variant="ghost" onClick={onClose}>Close</Button>}
    >
      <p className="muted" style={{ fontSize: 'var(--text-sm)', marginTop: 0 }}>
        The dose shown is what is taken each time; the figure beside it is the
        day's total, which is what gets dispensed and billed.
      </p>
      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th>Day</th><th>Date</th>
              {named.map(i => <th key={i.key}>{i.product!.label}</th>)}
              <th>Day total</th>
            </tr>
          </thead>
          <tbody>
            {dates.map((d, i) => (
              <tr key={d}>
                <td>{i + 1}</td>
                <td>{fmtDate(d)}</td>
                {named.map(it => (
                  <td key={it.key}>
                    {fmtQty(Number(it.quantity))}
                    <span className="dim"> × {it.timesPerDay} = </span>
                    <b>{fmtQty(dailyTotal(it))}</b>
                  </td>
                ))}
                <td><b>{fmtQty(coursePerDay(course))}</b></td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={2}><b>Total</b></td>
              {named.map(it => (
                <td key={it.key}><b>{fmtQty(dailyTotal(it) * dates.length)}</b></td>
              ))}
              <td><b>{fmtQty(courseTotal(course))}</b></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </Modal>
  )
}
